import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Segment } from 'semantic-ui-react';
import { useLocation } from 'react-router-dom';

import ReportIssue from 'src/components/coreUI/ReportIssue';
import { useAppSelector } from 'src/store';
import { PATH } from 'src/constants/app';
import Materials from 'src/components/coreUI/ClassSider/Materials';
import Modules from 'src/components/coreUI/ClassSider/Modules';
import { classByIdSelector } from 'src/store/model/classes';
import { isScopedTeacherSelector } from 'src/utils/helper';

import Head from './Head';
import {
  hasStaffCourseAccess,
  hideProgrammeBios,
} from 'src/utils/courseCapabilities';

const ClassSider: React.FC = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [headHeight, setHeadHeight] = useState<number>(0);

  const { pathname } = useLocation();

  const currentClass = useAppSelector((state) =>
    classByIdSelector(state, pathname.split('/')[2]),
  );

  const { menu, activeKey } = useAppSelector((state) => {
    // CISL teachers also answer module questions through Forum.
    const isTeacher =
      isScopedTeacherSelector(state) &&
      (hasStaffCourseAccess(currentClass) ||
        currentClass?.can_edit_curriculum === undefined);
    const teacherKeys = ['undefined', 'programme-bios', 'modules'];
    if (currentClass?.type === 'CISL') teacherKeys.push('discussions');

    return {
      menu: state.ui.menu.data[PATH.CLASS]
        .filter((m) => m.type === currentClass?.type || !m.type)
        .filter(
          (m) =>
            currentClass?.type !== 'CISL' ||
            ['undefined', 'programme-bios', 'modules', 'discussions'].includes(
              String(m.key),
            ),
        )
        .filter((m) => !isTeacher || teacherKeys.includes(String(m.key)))
        .filter(
          (m) =>
            m.key !== 'programme-bios' ||
            !hideProgrammeBios(currentClass, state.ui.app.me?.email),
        )
        .map((m) => {
          if (m.key === 'homework' && currentClass?.type === 'ACCELERATOR') {
            return { ...m, content: 'ASSIGNMENT' };
          }
          if (
            m.key === 'modules' &&
            (currentClass?.type === 'ACCELERATOR' ||
              currentClass?.type === 'CISL')
          ) {
            return { ...m, content: 'CURRICULUM' };
          }

          if (m.key === 'discussions' && currentClass?.type === 'CISL') {
            return { ...m, content: 'FORUM' };
          }
          return m;
        }),
      activeKey: String(pathname.split('/')[3] || undefined),
    };
  });

  const shouldShowMenu = useMemo(() => {
    return menu.map((m) => m.key).includes(activeKey);
  }, [activeKey, menu]);

  useEffect(() => {
    if (ref.current) {
      setHeadHeight(ref.current?.offsetHeight);
    }
  }, [window.innerHeight]);

  return (
    <Segment className="full_height d-flex-between d-column">
      <Head ref={ref} />
      <div
        className="flex-1"
        style={{
          maxHeight: window.innerHeight - headHeight - 145,
          overflowY: 'scroll',
        }}
      >
        {shouldShowMenu ? (
          <Materials
            menu={menu}
            activeKey={activeKey}
            bios={currentClass?.cisl_content?.bios}
          />
        ) : (
          <Modules />
        )}
      </div>
      <ReportIssue />
    </Segment>
  );
};

export default ClassSider;
